import React from 'react'

import { Icon, CubeAIOutline, Community, CubeOutline, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, shape, string, bool } from 'prop-types'

import { clearGenerationResult } from 'Pods/Shared/GenerationStatus/actions'
import FreeTrialTag from 'Pods/UnifiedNavigation/Shared/FreeTrialTag/FreeTrialTag'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

const ICON_SIZE = { width: '20', height: '20' }
const PREVIEW_ICON_SIZE = { width: '24', height: '24' }

// String icon keys (from the backend `create_ai_links`) -> Triangle UI glyphs.
const ICON_MAP = { cube_ai: CubeAIOutline, community: Community }

const SUCCEEDED = 'succeeded'

// The finished-run result row appended to the dropdown once a generation settles:
// success links to the generated model with its preview; error links back to the
// studio with a placeholder glyph (a failed run has no shareable model). Opening
// either clears the result (per product: it persists until opened or the next run).
function GenerationResult({ lastResult, labels }) {
  const isSuccess = lastResult.status === SUCCEEDED
  const href = isSuccess ? lastResult.pathname : labels.errorHref
  const label = isSuccess ? labels.readyTitle : labels.errorTitle
  const previewUrl = isSuccess ? lastResult.previewUrl : undefined

  return (
    <div className="unified-nav__dropdown-result-section">
      <a
        className={classnames(
          'unified-nav__dropdown-result',
          isSuccess ? 'unified-nav__dropdown-result--success' : 'unified-nav__dropdown-result--error'
        )}
        href={href}
        data-track-label="Create with AI"
        data-track-text={isSuccess ? 'Generation ready' : 'Generation issue'}
        onClick={clearGenerationResult}
      >
        <span className="unified-nav__dropdown-result-preview">
          {previewUrl ? (
            <img className="unified-nav__dropdown-result-image" src={previewUrl} alt="" />
          ) : (
            <Icon notSpaced color="dark" size={PREVIEW_ICON_SIZE} svgComponent={CubeOutline} />
          )}
        </span>
        <span className="unified-nav__dropdown-result-label cgt-body-base-semibold">{label}</span>
      </a>
    </div>
  )
}

GenerationResult.propTypes = {
  lastResult: shape({
    status: string,
    pathname: string,
    previewUrl: string,
  }).isRequired,
  labels: shape({
    readyTitle: string,
    errorTitle: string,
    errorHref: string,
  }).isRequired,
}

// "Create with AI" dropdown panel: a flat list anchored to the Create with AI
// trigger. Reuses the shared `.unified-nav__dropdown` card so it matches the
// Sellers Hub panel and renders in-flow inside the stacked mobile hamburger.
// The FREE TRIAL tag shows while a free trial is in progress or still attainable
// for the viewer (see Generator::SubscriptionState::FREE_TRIAL_TAG_TIERS). When a
// 3D-model generation has finished, a result row is appended below the links.
function CreateAiDropdown({
  id = undefined,
  links = EMPTY_ARRAY,
  showFreeTrialTag = false,
  lastResult = null,
  labels = EMPTY_OBJECT,
}) {
  return (
    <div className="unified-nav__dropdown" id={id}>
      <ul className="unified-nav__dropdown-list">
        {links.map((link) => {
          const IconComponent = ICON_MAP[link.icon]

          return (
            <li key={link.id} className="unified-nav__dropdown-item">
              <a
                className="unified-nav__dropdown-link cgt-body-base-semibold"
                href={link.href}
                data-track-label="Create with AI"
                data-track-text={link.trackText}
              >
                <span className="unified-nav__dropdown-item-icon">
                  {IconComponent && (
                    <Icon notSpaced color="dark" size={ICON_SIZE} svgComponent={IconComponent} />
                  )}
                </span>
                <span className="unified-nav__dropdown-item-label">{link.title}</span>
                {showFreeTrialTag && link.tag && <FreeTrialTag label={link.tag} />}
              </a>
            </li>
          )
        })}
      </ul>
      {lastResult && <GenerationResult lastResult={lastResult} labels={labels} />}
    </div>
  )
}

CreateAiDropdown.propTypes = {
  id: string,
  links: arrayOf(shape({
    id: string,
    href: string,
    title: string,
    trackText: string,
    icon: string,
    tag: string,
  })),
  showFreeTrialTag: bool,
  lastResult: shape({
    status: string,
    pathname: string,
    previewUrl: string,
  }),
  labels: shape({
    readyTitle: string,
    errorTitle: string,
    errorHref: string,
  }),
}

export default CreateAiDropdown
